import React, { useState, useEffect, useRef } from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import programsData from '../../../util/programs.json';

// Using Material-UI default theme colors for better visual balance
const THEME_COLORS = {
  PRIMARY: '#1976d2',      // Material-UI primary blue
  SECONDARY: '#dc004e',    // Material-UI secondary pink
  SUCCESS: '#2e7d32',      // Material-UI success green
  WARNING: '#ed6c02',      // Material-UI warning orange
  ERROR: '#d32f2f',        // Material-UI error red
  INFO: '#0288d1',         // Material-UI info blue
  GRAY: '#9e9e9e',         // Material-UI gray
  LIGHT_GRAY: '#f5f5f5'    // Light background
};

// Transform programs data to match expected format
const ALL_PROGRAMS = programsData.programs.map(program => ({
  id: program.key,
  name: program.value,
  description: program.description,
  isTopN: program.isTopN
}));

// Filter programs that can be selected in Top 3
const TOP_N_PROGRAMS = ALL_PROGRAMS.filter(program => program.isTopN === true);

const LIKERT_SCALE = [
  { value: 1, label: 'Not Interested' },
  { value: 2, label: 'Slightly Interested' },
  { value: 3, label: 'Moderately Interested' },
  { value: 4, label: 'Very Interested' },
  { value: 5, label: 'Extremely Interested' }
];

/**
 * ScholarPreferencesForm component - interactive form for StudentDashboard
 * @param {Object} props
 * @param {Object} [props.preferences] - Current preferences object
 * @param {function} [props.onChange] - Callback when preferences change
 * @param {function} [props.onSubmit] - Callback when form is submitted
 * @param {function} [props.onCancel] - Callback when user cancels
 */
function ScholarPreferencesForm({
  preferences = {},
  onChange,
  onSubmit,
  onCancel
}) {
  const [localPreferences, setLocalPreferences] = useState(preferences);
  const [topThree, setTopThree] = useState(preferences.topThree || []);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Focus management for accessibility
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    const scrollToStep = () => {
      const preferencesStep = document.querySelector('[data-step-id="preferences"]');
      if (preferencesStep) {
        // Scroll to step header with some offset to show the title clearly
        const rect = preferencesStep.getBoundingClientRect();
        const offset = 20; // Small offset from top
        window.scrollTo({
          top: window.pageYOffset + rect.top - offset,
          behavior: 'smooth'
        });
      } else if (formRef.current) {
        // Fallback: scroll to form if step not found
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    };

    // Wait for DOM to be ready, then scroll
    requestAnimationFrame(() => {
      setTimeout(scrollToStep, 100);
    });

    // Focus the first input for accessibility after scroll completes
    // Use preventScroll to avoid interfering with our step scroll
    setTimeout(() => {
      if (formRef.current) {
        const firstInput = formRef.current.querySelector('input, select, textarea');
        if (firstInput) {
          firstInput.focus({ preventScroll: true });
        }
      }
    }, 800);
  }, []);

  const handleTopThreeSelection = (programId) => {
    let newTopThree = [...topThree];

    if (newTopThree.includes(programId)) {
      // Remove from top three
      newTopThree = newTopThree.filter(id => id !== programId);
    } else if (newTopThree.length < 3) {
      // Add to top three if under limit
      newTopThree.push(programId);
    } else {
      // Replace the last item if at limit
      newTopThree[2] = programId;
    }

    setTopThree(newTopThree);
    updatePreferences(newTopThree, localPreferences);
  };

  const handleRatingChange = (programId, rating) => {
    const newRatings = {
      ...localPreferences,
      [programId]: rating
    };

    setLocalPreferences(newRatings);
    updatePreferences(topThree, newRatings);

    // Clear error for this program if it exists
    if (errors[programId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[programId];
        return newErrors;
      });
    }
  };

  const updatePreferences = (newTopThree, newRatings) => {
    const combinedPreferences = {
      topThree: newTopThree,
      ratings: newRatings
    };

    if (onChange) {
      onChange(combinedPreferences);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (topThree.length === 0) {
      newErrors.general = 'Please select at least one top choice program to continue.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit({
          topThree,
          ratings: localPreferences
        });
      }
    } catch (error) {
      console.error('Error submitting preferences:', error);
      setErrors({ general: 'Failed to submit preferences. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getLikertLabel = (value) => {
    const option = LIKERT_SCALE.find(opt => opt.value === value);
    return option ? option.label : '';
  };

  const isInTopThree = (programId) => topThree.includes(programId);
  const getTopThreeRank = (programId) => topThree.indexOf(programId) + 1;

  return (
    <Mui.Box
      ref={formRef}
      sx={{
        maxWidth: '900px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 2,
        minHeight: '600px',
        position: 'relative'
      }}
      role="form"
      aria-labelledby="scholar-preferences-title"
      tabIndex={-1}
    >
      {/* Live region for form validation announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {errors.general && `Form error: ${errors.general}`}
        {topThree.length === 3 && 'Top 3 programs selected successfully'}
      </div>

      <Mui.Typography
        variant="h3"
        component="h1"
        id="scholar-preferences-title"
        sx={{
          marginBottom: '24px',
          fontWeight: 600,
          fontSize: '2rem',
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          lineHeight: 1.2,
          margin: '0 0 24px 0',
          color: 'text.primary'
        }}
      >
        Scholar Program Preferences
      </Mui.Typography>

      {/* Error Message */}
      {errors.general && (
        <Mui.Alert
          severity="warning"
          sx={{ mb: 3 }}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <Mui.AlertTitle>Form Validation Error</Mui.AlertTitle>
          {errors.general}
        </Mui.Alert>
      )}

      {/* Top 3 Selection Section */}
      <Mui.Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Mui.Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Select Your Top 3 Program Choices
        </Mui.Typography>
        <Mui.Typography variant="body2" paragraph color="textSecondary">
          Click on programs to select your top 3 choices. These will be prioritized in program matching.
        </Mui.Typography>

        {/* Top 3 Progress */}
        <Mui.Box sx={{ mb: 2 }}>
          <Mui.Typography variant="body2" color="textSecondary">
            Selected: {topThree.length} of 3
          </Mui.Typography>
          <Mui.LinearProgress
            variant="determinate"
            value={(topThree.length / 3) * 100}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3
            }}
          />
        </Mui.Box>

        {/* Top 3 Programs Grid */}
        <Mui.Grid container spacing={{ xs: 2, sm: 3 }}>
          {TOP_N_PROGRAMS.map((program) => {
            const isSelected = isInTopThree(program.id);
            const rank = getTopThreeRank(program.id);

            return (
              <Mui.Grid item xs={12} sm={6} md={4} key={program.id}>
                <Mui.Card
                  variant="outlined"
                  component="button"
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-describedby={`program-${program.id}-description`}
                  aria-label={`${isSelected ? 'Remove' : 'Select'} ${program.name} ${isSelected ? `as top choice #${rank}` : 'as a top 3 program choice'}`}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    cursor: 'pointer',
                    border: isSelected ? `2px solid ${THEME_COLORS.PRIMARY}` : '1px solid #e0e0e0',
                    height: '100%',
                    minHeight: { xs: '160px', sm: '140px' },
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: THEME_COLORS.PRIMARY,
                      transform: 'translateY(-1px)',
                      boxShadow: 2
                    },
                    '&:focus': {
                      outline: `2px solid ${THEME_COLORS.PRIMARY}`,
                      outlineOffset: '1px',
                      boxShadow: 3
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                      transition: 'transform 0.1s ease'
                    },
                    position: 'relative',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? 3 : 1
                  }}
                  onClick={() => handleTopThreeSelection(program.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTopThreeSelection(program.id);
                    }
                  }}
                >
                  {isSelected && (
                    <Mui.Chip
                      label={`#${rank}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: THEME_COLORS.PRIMARY,
                        color: 'white'
                      }}
                    />
                  )}

                  <Mui.Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Mui.Typography variant="subtitle1" gutterBottom>
                      {program.name}
                    </Mui.Typography>
                    <Mui.Typography
                      variant="body2"
                      color="textSecondary"
                      id={`program-${program.id}-description`}
                      sx={{ flex: 1 }}
                    >
                      {program.description}
                    </Mui.Typography>

                    {isSelected && (
                      <Mui.Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'center' }}>
                        <MuiIcons.CheckCircle sx={{ mr: 1, fontSize: '1rem', color: THEME_COLORS.SUCCESS }} />
                        <Mui.Typography variant="caption" sx={{ color: THEME_COLORS.SUCCESS }}>
                          Top Choice #{rank}
                        </Mui.Typography>
                      </Mui.Box>
                    )}
                  </Mui.Box>
                </Mui.Card>
              </Mui.Grid>
            );
          })}
        </Mui.Grid>

        {/* Selected Top 3 Summary */}
        {topThree.length > 0 && (
          <Mui.Box sx={{ mt: 2, p: { xs: 2, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Mui.Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Your Top Choices:
            </Mui.Typography>
            <Mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {topThree.map((programId, index) => {
                const program = ALL_PROGRAMS.find(p => p.id === programId);
                return (
                  <Mui.Chip
                    key={programId}
                    label={`#${index + 1} ${program?.name}`}
                    variant="filled"
                    size="small"
                    onDelete={() => handleTopThreeSelection(programId)}
                    sx={{
                      backgroundColor: THEME_COLORS.PRIMARY,
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.8)'
                        }
                      }
                    }}
                  />
                );
              })}
            </Mui.Box>
          </Mui.Box>
        )}
      </Mui.Card>

      {/* Likert Scale Section for Remaining Programs */}
      <Mui.Box sx={{ mt: 3 }}>
        <Mui.Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Rate Your Interest in Other Programs
        </Mui.Typography>
        <Mui.Typography variant="body2" paragraph color="textSecondary">
          Rate your interest level for the remaining programs using the 1-5 scale.
        </Mui.Typography>

        {/* Rating Scale Legend */}
        <Mui.Box sx={{ mb: 3, p: { xs: 2, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Mui.Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Rating Scale:
          </Mui.Typography>
          <Mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {LIKERT_SCALE.map((option) => (
              <Mui.Chip
                key={option.value}
                label={`${option.value} - ${option.label}`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: THEME_COLORS.GRAY,
                  color: THEME_COLORS.GRAY
                }}
              />
            ))}
          </Mui.Box>
        </Mui.Box>

        {/* Remaining Programs for Rating */}
        <Mui.Grid container spacing={{ xs: 2, sm: 3 }}>
          {ALL_PROGRAMS
            .filter(program => !isInTopThree(program.id))
            .map((program) => {
              const currentRating = localPreferences[program.id] || 0;

              return (
                <Mui.Grid item xs={12} md={6} key={program.id}>
                  <Mui.Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      p: { xs: 2, sm: 3 },
                      border: currentRating > 0 ? `2px solid ${THEME_COLORS.PRIMARY}` : '1px solid #e0e0e0',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: THEME_COLORS.PRIMARY,
                        boxShadow: 1
                      }
                    }}
                  >
                    <Mui.Box sx={{ mb: 2 }}>
                      <Mui.Typography variant="subtitle1" gutterBottom>
                        {program.name}
                      </Mui.Typography>
                      <Mui.Typography variant="body2" color="textSecondary">
                        {program.description}
                      </Mui.Typography>
                    </Mui.Box>

                    <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Mui.Typography variant="body2" sx={{ minWidth: '120px' }}>
                        Interest Level:
                      </Mui.Typography>

                      <Mui.Rating
                        value={currentRating}
                        onChange={(_, newValue) => handleRatingChange(program.id, newValue || 0)}
                        max={5}
                        size="large"
                        sx={{
                          mr: 2,
                          '& .MuiRating-iconFilled': {
                            color: THEME_COLORS.WARNING
                          },
                          '& .MuiRating-iconHover': {
                            color: THEME_COLORS.WARNING
                          },
                          '& .MuiRating-icon': {
                            fontSize: '2rem'
                          }
                        }}
                        aria-label={`Rate your interest in ${program.name}`}
                      />

                      {currentRating > 0 && (
                        <Mui.Chip
                          label={getLikertLabel(currentRating)}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: THEME_COLORS.WARNING,
                            color: THEME_COLORS.WARNING
                          }}
                        />
                      )}
                    </Mui.Box>
                  </Mui.Card>
                </Mui.Grid>
              );
            })}
        </Mui.Grid>
      </Mui.Box>

      {/* Action Buttons */}
      <Mui.Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          zIndex: 10,
          flexDirection: { xs: 'column', sm: 'row' }
        }}
      >
        <Mui.Button
          variant="outlined"
          onClick={handleCancel}
          startIcon={<MuiIcons.ArrowBack />}
          disabled={isSubmitting}
          sx={{
            minHeight: '48px',
            fontSize: '1rem',
            order: { xs: 2, sm: 1 },
            borderColor: THEME_COLORS.GRAY,
            color: THEME_COLORS.GRAY,
            '&:hover': {
              borderColor: THEME_COLORS.PRIMARY,
              color: THEME_COLORS.PRIMARY
            }
          }}
        >
          Cancel
        </Mui.Button>

        <Mui.Button
          variant="contained"
          onClick={handleSubmit}
          disabled={topThree.length === 0 || isSubmitting}
          endIcon={isSubmitting ? <Mui.CircularProgress size={16} sx={{ color: 'white' }} /> : <MuiIcons.Send />}
          sx={{
            minHeight: '48px',
            fontSize: '1rem',
            fontWeight: 600,
            order: { xs: 1, sm: 2 },
            backgroundColor: THEME_COLORS.PRIMARY,
            color: 'white',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: 6
            },
            '&:disabled': {
              backgroundColor: THEME_COLORS.GRAY,
              color: 'white',
              opacity: 0.6
            }
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Preferences'}
        </Mui.Button>
      </Mui.Box>

      {/* Help Text */}
      <Mui.Box sx={{ mt: 3, p: { xs: 2, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Mui.Typography variant="body2" color="textSecondary">
          <strong>How it works:</strong> Your top 3 choices will be prioritized for program matching.
          The rating scale helps us understand your broader interests for alternative placements.
        </Mui.Typography>
      </Mui.Box>
    </Mui.Box>
  );
}

export default ScholarPreferencesForm;